import React, { useState, useEffect, useCallback } from 'react';
import { Card, ListGroup, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { Shield, Clock, Person, ArrowClockwise } from 'react-bootstrap-icons';
import api from '../services/api';

const RecentLogins = ({ limit = 3, showTitle = true }) => {
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRecentLogins = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await api.get(`/api/my/recent-logins?limit=${limit}`);
      
      if (response.data.success) {
        setLogins(response.data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError('Failed to fetch recent logins');
      }
    } catch (err) {
      console.error('Error fetching recent logins:', err);
      setError('Error loading recent logins');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecentLogins();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRecentLogins(true);
    }, 30000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [fetchRecentLogins]);

  const handleManualRefresh = () => {
    fetchRecentLogins(true);
  };

  const getRoleBadge = (role) => {
    const variants = {
      'admin': 'danger',
      'distributor': 'warning',
      'user': 'primary'
    };
    return (
      <Badge bg={variants[role] || 'secondary'} className="ms-2">
        {role}
      </Badge>
    );
  };

  const formatTimeAgo = (timeAgo) => {
    if (timeAgo.includes('minute')) {
      const minutes = timeAgo.match(/\d+/)?.[0] || '0';
      return `${minutes} min ago`;
    } else if (timeAgo.includes('hour')) {
      const hours = timeAgo.match(/\d+/)?.[0] || '0';
      return `${hours} hour${hours !== '1' ? 's' : ''} ago`;
    } else if (timeAgo.includes('day')) {
      const days = timeAgo.match(/\d+/)?.[0] || '0';
      return `${days} day${days !== '1' ? 's' : ''} ago`;
    }
    return timeAgo;
  };

  const formatLastUpdated = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) !== 1 ? 's' : ''} ago`;
    return date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Card className="h-100">
        {showTitle && (
          <Card.Header className="bg-success text-white">
            <h5 className="mb-0">
              <Shield className="me-2" />
              Recent Logins
            </h5>
          </Card.Header>
        )}
        <Card.Body className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="success" />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-100">
        {showTitle && (
          <Card.Header className="bg-success text-white">
            <h5 className="mb-0">
              <Shield className="me-2" />
              Recent Logins
            </h5>
          </Card.Header>
        )}
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
          <div className="text-center">
            <Button variant="outline-success" size="sm" onClick={handleManualRefresh}>
              <ArrowClockwise className="me-1" />
              Retry
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      {showTitle && (
        <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <Shield className="me-2" />
            Recent Logins
          </h5>
          <div className="d-flex align-items-center">
            {refreshing && (
              <Spinner animation="border" size="sm" variant="light" className="me-2" />
            )}
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-1"
            >
              <ArrowClockwise className={refreshing ? 'spinning' : ''} />
            </Button>
          </div>
        </Card.Header>
      )}
      <Card.Body className="p-0">
        {logins.length === 0 ? (
          <div className="text-center py-4">
            <Shield size={48} className="text-muted mb-3" />
            <p className="text-muted">No recent logins</p>
          </div>
        ) : (
          <ListGroup variant="flush">
            {logins.map((login, index) => (
              <ListGroup.Item 
                key={login.id} 
                className={`d-flex align-items-center py-3 ${
                  index === 0 ? 'border-top-0' : ''
                }`}
                style={{
                  borderLeft: 'none',
                  borderRight: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="me-3">
                  <Shield className="text-success" size={20} />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-1 fw-bold text-dark">
                        {login.description}
                      </p>
                      <div className="d-flex align-items-center">
                        <Person size={14} className="text-muted me-1" />
                        <small className="text-muted">
                          {login.user_name}
                        </small>
                        {getRoleBadge(login.user_role)}
                        <small className="text-muted ms-2">
                          {login.user_email}
                        </small>
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="d-flex align-items-center">
                        <Clock size={14} className="text-muted me-1" />
                        <small className="text-muted">
                          {formatTimeAgo(login.time_ago)}
                        </small>
                      </div>
                      <small className="text-muted">
                        {login.formatted_date_time}
                      </small>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
      {logins.length > 0 && (
        <Card.Footer className="bg-light text-center">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Last {logins.length} login{logins.length !== 1 ? 's' : ''}
            </small>
            {lastUpdated && (
              <small className="text-muted">
                Updated {formatLastUpdated(lastUpdated)}
              </small>
            )}
          </div>
        </Card.Footer>
      )}
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default RecentLogins;
